import { useEffect, useRef } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";
import { useContent } from "@/content/ContentContext";

const Stars = () => (
  <span className="flex items-center gap-0.5 text-bordeaux" aria-hidden="true">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="w-3 h-3 fill-current" strokeWidth={1} />
    ))}
  </span>
);

const ReviewsSection = () => {
  const { lang, t } = useLang();
  const r = t.reviews;
  const { reviews, reviewsMeta } = useContent();
  const track = useRef(null);
  const paused = useRef(false);
  const resume = useRef(null);

  // Scorrimento automatico continuo (loop senza salti), in pausa durante l'interazione.
  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(now - last, 50);
      last = now;
      const el = track.current;
      if (el && !paused.current && el.scrollWidth > el.clientWidth) {
        const cards = el.children;
        const n = cards.length / 2;
        const half = n >= 1 ? cards[n].offsetLeft - cards[0].offsetLeft : el.scrollWidth / 2;
        let next = el.scrollLeft + dt * 0.06;
        if (next >= half) next -= half;
        el.scrollLeft = next;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const hold = () => {
    paused.current = true;
    clearTimeout(resume.current);
  };
  const release = (delay = 2000) => {
    clearTimeout(resume.current);
    resume.current = setTimeout(() => (paused.current = false), delay);
  };

  const scrollBy = (dir) => {
    const el = track.current;
    if (!el) return;
    hold();
    const card = el.querySelector("figure");
    const step = card ? card.offsetWidth + 32 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    release(2500);
  };

  const drag = useRef(null);
  const onPointerDown = (e) => {
    hold();
    if (e.pointerType !== "mouse") return;
    drag.current = { x: e.clientX, left: track.current.scrollLeft };
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    track.current.scrollLeft = drag.current.left - (e.clientX - drag.current.x);
  };
  const endDrag = () => {
    drag.current = null;
    release();
  };

  return (
    <section id="recensioni" className="py-20 md:py-28 border-t border-sand/70 overflow-hidden" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="md:flex md:items-end md:justify-between gap-10">
          <div>
            <p className="eyebrow text-olive mb-7">{r.label}</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-2xl">
              {r.title}
            </h2>
          </div>
          <a
            href={reviewsMeta.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-source-link"
            className="mt-8 md:mt-0 inline-flex items-baseline gap-3 group"
          >
            <span className="font-serif text-5xl md:text-6xl leading-none">{reviewsMeta.rating}</span>
            <span className="text-[11px] uppercase tracking-[0.25em] text-anthracite/55 leading-snug group-hover:text-bordeaux transition-colors duration-300">
              / 5 · {reviewsMeta.count} {r.reviewsOn} {reviewsMeta.source}
              <ArrowUpRight className="inline w-3.5 h-3.5 ml-1 -mt-0.5" />
            </span>
          </a>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-14 md:mt-20">
        <div
          ref={track}
          data-testid="reviews-strip"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onTouchStart={hold}
          onTouchEnd={() => release()}
          className="flex gap-6 md:gap-8 overflow-x-auto no-scrollbar px-5 md:px-10 pb-2 cursor-grab active:cursor-grabbing select-none"
        >
          {[...reviews, ...reviews].map((rev, i) => (
            <figure
              key={i}
              data-testid={i < reviews.length ? `review-card-${i}` : `review-card-dup-${i - reviews.length}`}
              data-edit-reviews={i % reviews.length}
              aria-hidden={i >= reviews.length}
              className="w-[300px] sm:w-[360px] md:w-[420px] min-h-[300px] md:min-h-[320px] shrink-0 border border-sand/80 bg-ivory p-7 md:p-9 flex flex-col"
            >
              <Stars />
              <blockquote className="mt-5 flex-1 font-serif text-lg md:text-xl leading-snug text-anthracite">
                “{rev[lang]}”
              </blockquote>
              <figcaption className="mt-6 pt-5 border-t border-sand/70 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-anthracite/55">
                <span>{rev.author}</span>
                <span>{rev.date[lang]}</span>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-10 mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label={r.prev}
            data-testid="reviews-prev"
            className="inline-flex items-center justify-center w-11 h-11 border border-sand/90 text-anthracite hover:bg-anthracite hover:text-ivory hover:border-anthracite transition-colors duration-300"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label={r.next}
            data-testid="reviews-next"
            className="inline-flex items-center justify-center w-11 h-11 border border-sand/90 text-anthracite hover:bg-anthracite hover:text-ivory hover:border-anthracite transition-colors duration-300"
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <span className="ml-3 text-[11px] uppercase tracking-[0.22em] text-anthracite/45">
            {r.swipeHint}
          </span>
        </div>
      </Reveal>
    </section>
  );
};

export default ReviewsSection;
